import React from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Sun, Moon, Monitor, Globe, Home, ListTodo, Timer, PenLine, Sparkles, Clock, Layout,
} from 'lucide-react';

const COMMON_TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Central Europe (CET)' },
  { value: 'Europe/Berlin', label: 'Berlin (CET)' },
  { value: 'Europe/Madrid', label: 'Madrid (CET)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Asia/Shanghai', label: 'China (CST)' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST)' },
  { value: 'Australia/Melbourne', label: 'Melbourne (AEST)' },
  { value: 'Pacific/Auckland', label: 'New Zealand (NZST)' },
  { value: 'America/Mexico_City', label: 'Mexico City (CST)' },
  { value: 'America/Sao_Paulo', label: 'São Paulo (BRT)' },
  { value: 'UTC', label: 'UTC' },
];

/**
 * Consolidated "Display" card combining:
 *   Appearance (theme), Language, Opening Screen, Timezone.
 *
 * All props are state + handlers from the parent SettingsPage so we don't
 * fragment localStorage/API logic.
 */
const DisplaySettingsCard = ({
  isSpanish,
  theme, onThemeChange,
  defaultLanguage, onLanguageChange,
  openingScreen, onOpeningScreenChange,
  userTimezone, onTimezoneChange, isSavingTimezone,
}) => {
  const themeIcon = theme === 'dark' ? <Moon className="h-4 w-4" />
    : theme === 'light' ? <Sun className="h-4 w-4" />
    : <Monitor className="h-4 w-4" />;

  return (
    <Card className="mb-6" data-testid="display-settings-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Layout className="h-5 w-5" />
          {isSpanish ? 'Pantalla' : 'Display'}
        </CardTitle>
        <CardDescription>
          {isSpanish
            ? 'Tema, idioma, pantalla de inicio y zona horaria'
            : 'Theme, language, opening screen & timezone'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-1">
        {/* Theme */}
        <div className="flex items-center justify-between py-3">
          <Label className="flex items-center gap-2">
            {themeIcon}
            {isSpanish ? 'Tema' : 'Theme'}
          </Label>
          <Select value={theme} onValueChange={onThemeChange}>
            <SelectTrigger className="w-40" data-testid="theme-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="light">
                <div className="flex items-center gap-2"><Sun className="h-4 w-4" />{isSpanish ? 'Claro' : 'Light'}</div>
              </SelectItem>
              <SelectItem value="dark">
                <div className="flex items-center gap-2"><Moon className="h-4 w-4" />{isSpanish ? 'Oscuro' : 'Dark'}</div>
              </SelectItem>
              <SelectItem value="system">
                <div className="flex items-center gap-2"><Monitor className="h-4 w-4" />{isSpanish ? 'Sistema' : 'System'}</div>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Separator />

        {/* Language */}
        <div className="flex items-center justify-between py-3">
          <Label className="flex items-center gap-2">
            <Globe className="h-4 w-4" />
            {isSpanish ? 'Idioma' : 'Language'}
          </Label>
          <Select value={defaultLanguage} onValueChange={onLanguageChange}>
            <SelectTrigger className="w-40" data-testid="language-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="en">English</SelectItem>
              <SelectItem value="es">Español</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Separator />

        {/* Opening Screen */}
        <div className="flex items-center justify-between py-3">
          <Label className="flex items-center gap-2">
            <Home className="h-4 w-4" />
            {isSpanish ? 'Pantalla de inicio' : 'Opening Screen'}
          </Label>
          <Select value={openingScreen} onValueChange={onOpeningScreenChange}>
            <SelectTrigger className="w-44" data-testid="opening-screen-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="home"><div className="flex items-center gap-2"><Home className="h-4 w-4" />{isSpanish ? 'Inicio' : 'Home'}</div></SelectItem>
              <SelectItem value="library"><div className="flex items-center gap-2"><Home className="h-4 w-4" />{isSpanish ? 'Biblioteca' : 'Library'}</div></SelectItem>
              <SelectItem value="todo"><div className="flex items-center gap-2"><ListTodo className="h-4 w-4" />{isSpanish ? 'Tareas' : 'To-Do List'}</div></SelectItem>
              <SelectItem value="timer"><div className="flex items-center gap-2"><Timer className="h-4 w-4" />{isSpanish ? 'Temporizador' : 'Timer'}</div></SelectItem>
              <SelectItem value="blog"><div className="flex items-center gap-2"><PenLine className="h-4 w-4" />Blog</div></SelectItem>
              <SelectItem value="interpreter"><div className="flex items-center gap-2"><Sparkles className="h-4 w-4" />{isSpanish ? 'Intérprete' : 'Interpreter'}</div></SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Separator />

        {/* Timezone */}
        <div className="py-3 space-y-2">
          <div className="flex items-center justify-between">
            <Label className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              {isSpanish ? 'Zona Horaria' : 'Timezone'}
            </Label>
            {isSavingTimezone && (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
            )}
          </div>
          <Select value={userTimezone} onValueChange={onTimezoneChange} disabled={isSavingTimezone}>
            <SelectTrigger data-testid="timezone-select">
              <SelectValue placeholder={isSpanish ? 'Selecciona zona horaria' : 'Select timezone'} />
            </SelectTrigger>
            <SelectContent>
              {COMMON_TIMEZONES.map((tz) => (
                <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            {isSpanish
              ? `Tu día y racha se reinician a las 12:00 AM (${userTimezone || '...'})`
              : `Your day & streak reset at 12:00 AM (${userTimezone || '...'})`}
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default DisplaySettingsCard;
